import { _ as _ts_decorate } from "@swc/helpers/_/_ts_decorate";
import { _ as _ts_metadata } from "@swc/helpers/_/_ts_metadata";
import { action, computed, flow, observable, runInAction } from "mobx";
import { BlockerCollection } from "../models/blockerCollection.js";
import { BlockerTemplate } from "../models/blockerTemplate.js";
import { CookieGroupCollection } from "../models/cookieGroupCollection.js";
import { ServiceTemplate } from "../models/serviceTemplate.js";
import { request } from "../utils/request.js";
import { locationRestCookieUnassignedGet } from "../wp-api/cookieUnassigned.get.js";
import { locationRestTemplatesBlockersGet } from "../wp-api/templatesBlocker.get.js";
import { locationRestTemplatesServicesGet } from "../wp-api/templatesServices.get.js";
class CookieStore {
    get blockersCount() {
        return this.fetchedAllBlockers ? this.blockers.entries.size : this.rootStore.optionStore.allBlockerCount;
    }
    get cookiesCount() {
        return Array.from(this.groups.entries.values()).map(({ cookiesCount })=>cookiesCount).reduce((total, num)=>total + num, 0);
    }
    constructor(rootStore){
        this.busy = false;
        this.unassignedCookies = new Map();
        this.templatesBlocker = new Map();
        this.busyTemplatesBlocker = false;
        this.templatesServices = new Map();
        this.busyTemplatesServices = false;
        this.fetchedAllBlockers = false;
        this.fetchGroups = flow(function*() {
            yield this.groups.get({
                params: {
                    per_page: 100
                }
            });
            yield this.fetchUnassignedCookies();
        });
        this.fetchUnassignedCookies = flow(function*() {
            try {
                const items = yield request({
                    location: locationRestCookieUnassignedGet
                });
                // Save unassigned service as object
                for (const item of Object.values(items)){
                    this.unassignedCookies.set(item.id, item);
                }
            } catch (e) {
                console.log(e);
                throw e;
            }
        });
        this.fetchBlockers = flow(function*() {
            yield this.blockers.get({
                request: {
                    status: [
                        "draft",
                        "publish",
                        "private"
                    ]
                },
                params: {
                    per_page: 100,
                    context: "edit"
                }
            });
            this.fetchedAllBlockers = true;
        });
        this.fetchTemplatesBlocker = flow(function*(params) {
            this.busyTemplatesBlocker = true;
            try {
                const { items } = yield request({
                    location: locationRestTemplatesBlockersGet,
                    params
                });
                this.templatesBlocker.clear();
                this.addBlockerTemplates(items);
            } catch (e) {
                console.log(e);
                throw e;
            } finally{
                this.busyTemplatesBlocker = false;
            }
        });
        this.fetchTemplatesServices = flow(function*(params) {
            this.busyTemplatesServices = true;
            try {
                const { items } = yield request({
                    location: locationRestTemplatesServicesGet,
                    params
                });
                // Load other languages in a multilingual environment
                if ([
                    "redownload",
                    "invalidate"
                ].indexOf(params?.storage) > -1) {
                    const { activeLanguages, currentLanguage } = this.rootStore.optionStore.others;
                    for (const activeLanguage of activeLanguages){
                        if (activeLanguage !== currentLanguage) {
                            yield request({
                                location: locationRestTemplatesServicesGet,
                                params: {
                                    ...params,
                                    _dataLocale: activeLanguage
                                }
                            });
                        }
                    }
                }
                this.templatesServices.clear();
                this.addServiceTemplates(items);
            } catch (e) {
                console.log(e);
                throw e;
            } finally{
                this.busyTemplatesServices = false;
            }
        });
        this.rootStore = rootStore;
        runInAction(()=>{
            this.groups = new CookieGroupCollection(this);
            this.blockers = new BlockerCollection(this);
        });
    }
    get essentialGroup() {
        if (this.groups.entries.size === 0) {
            return undefined;
        }
        const it = this.groups.entries.values();
        let group;
        while(group = it.next().value){
            if (group.data.meta.isEssential) {
                break;
            }
        }
        return group;
    }
    addBlockerTemplates(items) {
        for (const template of items){
            this.templatesBlocker.set(template.identifier, new BlockerTemplate(template, this));
        }
    }
    addServiceTemplates(items) {
        for (const template of items){
            this.templatesServices.set(template.identifier, new ServiceTemplate(template, this));
        }
    }
}
_ts_decorate([
    observable
], CookieStore.prototype, "busy", void 0);
_ts_decorate([
    observable,
    _ts_metadata("design:type", typeof CookieGroupCollection === "undefined" ? Object : CookieGroupCollection)
], CookieStore.prototype, "groups", void 0);
_ts_decorate([
    observable
], CookieStore.prototype, "unassignedCookies", void 0);
_ts_decorate([
    observable,
    _ts_metadata("design:type", typeof BlockerCollection === "undefined" ? Object : BlockerCollection)
], CookieStore.prototype, "blockers", void 0);
_ts_decorate([
    observable
], CookieStore.prototype, "templatesBlocker", void 0);
_ts_decorate([
    observable
], CookieStore.prototype, "busyTemplatesBlocker", void 0);
_ts_decorate([
    observable
], CookieStore.prototype, "templatesServices", void 0);
_ts_decorate([
    observable
], CookieStore.prototype, "busyTemplatesServices", void 0);
_ts_decorate([
    observable
], CookieStore.prototype, "fetchedAllBlockers", void 0);
_ts_decorate([
    computed,
    _ts_metadata("design:type", void 0),
    _ts_metadata("design:paramtypes", [])
], CookieStore.prototype, "blockersCount", null);
_ts_decorate([
    computed,
    _ts_metadata("design:type", void 0),
    _ts_metadata("design:paramtypes", [])
], CookieStore.prototype, "cookiesCount", null);
_ts_decorate([
    computed,
    _ts_metadata("design:type", void 0),
    _ts_metadata("design:paramtypes", [])
], CookieStore.prototype, "essentialGroup", null);
_ts_decorate([
    action,
    _ts_metadata("design:type", Function),
    _ts_metadata("design:paramtypes", [
        Array
    ]),
    _ts_metadata("design:returntype", void 0)
], CookieStore.prototype, "addBlockerTemplates", null);
_ts_decorate([
    action,
    _ts_metadata("design:type", Function),
    _ts_metadata("design:paramtypes", [
        Array
    ]),
    _ts_metadata("design:returntype", void 0)
], CookieStore.prototype, "addServiceTemplates", null);
export { CookieStore };
